"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ProvisionComponentRolloutStateAck = exports.ProvisionComponentRolloutStateReport = exports.ProvisionComponentRolloutStepResult = exports.ProvisionComponentRolloutWork = exports.ProvisionComponentRolloutPlan = exports.ProvisionComponentRolloutStep = exports.ProvisionComponentRollbackPoint = exports.ProvisionComponentRolloutAction = exports.ProvisionComponentRolloutExecutor = exports.ProvisionComponentDeliveryMode = void 0;
const zod_1 = require("zod");
const provision_composition_js_1 = require("./provision-composition.js");
const provision_js_1 = require("./provision.js");
exports.ProvisionComponentDeliveryMode = zod_1.z.enum([
    'bundled',
    'provision_artifact',
    'os_package',
    'external_service',
    'manual',
]);
exports.ProvisionComponentRolloutExecutor = zod_1.z.enum([
    'bos_artifact',
    'terminal',
    'os_package_manager',
    'external_service',
    'operator',
]);
exports.ProvisionComponentRolloutAction = zod_1.z.enum([
    'capture_rollback_point',
    'download_artifact',
    'verify_artifact',
    'stop_component',
    'install_component',
    'install_os_package',
    'configure_component',
    'start_component',
    'verify_bundled_component',
    'validate_external_service',
    'manual_confirmation',
    'health_check',
    'activate_composition',
]);
exports.ProvisionComponentRollbackPoint = zod_1.z
    .object({
    componentId: zod_1.z.string().min(1).max(160),
    componentVersionId: zod_1.z.string().min(1).max(160).nullable(),
    semanticVersion: zod_1.z.string().trim().min(1).max(80).nullable(),
    artifactId: zod_1.z.string().min(1).max(160).nullable(),
    checksum: zod_1.z
        .string()
        .regex(/^[a-f0-9]{64}$/)
        .nullable(),
})
    .strict();
exports.ProvisionComponentRolloutStep = zod_1.z
    .object({
    stepId: zod_1.z
        .string()
        .regex(/^[a-z0-9][a-z0-9_.:-]*$/)
        .max(255),
    componentId: zod_1.z.string().min(1).max(160).nullable(),
    componentVersionId: zod_1.z.string().min(1).max(160).nullable(),
    componentKey: provision_composition_js_1.ProvisionCompositionKey.nullable(),
    componentType: provision_composition_js_1.ProvisionComponentType.nullable(),
    semanticVersion: zod_1.z.string().trim().min(1).max(80).nullable(),
    deliveryMode: exports.ProvisionComponentDeliveryMode.nullable(),
    action: exports.ProvisionComponentRolloutAction,
    executor: exports.ProvisionComponentRolloutExecutor,
    dependsOn: zod_1.z.array(zod_1.z.string().min(1).max(255)).max(256),
    installAuthorized: zod_1.z.boolean(),
    activationPolicy: zod_1.z.enum(['live', 'when_idle', 'restart_required']),
    artifact: provision_composition_js_1.ProvisionComponentArtifactReference.nullable(),
    packageManifest: provision_composition_js_1.ProvisionPeripheralPackageManifest.nullable().optional(),
    rollbackPoint: exports.ProvisionComponentRollbackPoint.nullable(),
})
    .strict();
exports.ProvisionComponentRolloutPlan = zod_1.z
    .object({
    schemaVersion: provision_composition_js_1.ProvisionCompositionSchemaVersion,
    applicationInstanceId: zod_1.z.string().min(1).max(160),
    applicationType: zod_1.z.enum(['pos', 'sco', 'attendant']),
    platform: provision_composition_js_1.ProvisionCompositionKey,
    architecture: provision_composition_js_1.ProvisionCompositionKey,
    desiredCompositionRevision: zod_1.z.number().int().positive(),
    desiredCompositionChecksum: zod_1.z.string().regex(/^[a-f0-9]{64}$/),
    planChecksum: zod_1.z.string().regex(/^[a-f0-9]{64}$/),
    mode: zod_1.z.enum(['deploy', 'rollback']),
    componentVersionIds: zod_1.z.array(zod_1.z.string().min(1).max(160)).min(1).max(512),
    dependencyOrder: zod_1.z.array(zod_1.z.string().min(1).max(160)).min(1).max(512),
    strictestActivationPolicy: zod_1.z.enum(['live', 'when_idle', 'restart_required']),
    requiresRestart: zod_1.z.boolean(),
    hasOperatorSteps: zod_1.z.boolean(),
    installAuthorized: zod_1.z.boolean(),
    rollbackPoints: zod_1.z.array(exports.ProvisionComponentRollbackPoint).max(512),
    steps: zod_1.z.array(exports.ProvisionComponentRolloutStep).min(1).max(4096),
})
    .strict()
    .superRefine((plan, context) => {
    const ids = new Set();
    const dependencyOrder = new Set(plan.dependencyOrder);
    plan.steps.forEach((step, index) => {
        if (ids.has(step.stepId)) {
            context.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                path: ['steps', index, 'stepId'],
                message: `Duplicate rollout step ${step.stepId}`,
            });
        }
        ids.add(step.stepId);
        if (step.installAuthorized &&
            (step.deliveryMode !== 'provision_artifact' ||
                !['download_artifact', 'install_component'].includes(step.action))) {
            context.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                path: ['steps', index, 'installAuthorized'],
                message: 'Only Provision artifact download/install steps can authorize BOS installation',
            });
        }
        const requiresPackageManifest = step.action === 'install_component' &&
            step.componentType !== 'core' &&
            step.componentType !== 'ui';
        const packageManifest = step.packageManifest;
        if (packageManifest !== null && packageManifest !== undefined && !requiresPackageManifest) {
            context.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                path: ['steps', index, 'packageManifest'],
                message: 'Only deployable peripheral install steps carry their immutable package manifest',
            });
        }
        if (packageManifest !== null &&
            packageManifest !== undefined &&
            (packageManifest.componentId !== step.componentId ||
                packageManifest.componentVersionId !== step.componentVersionId ||
                packageManifest.componentType !== step.componentType ||
                step.deliveryMode !== 'provision_artifact')) {
            context.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                path: ['steps', index, 'packageManifest'],
                message: 'Package manifest identity must match its rollout step',
            });
        }
        if (step.componentVersionId !== null && !dependencyOrder.has(step.componentVersionId)) {
            context.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                path: ['steps', index, 'componentVersionId'],
                message: `Rollout step references out-of-scope version ${step.componentVersionId}`,
            });
        }
    });
    plan.steps.forEach((step, index) => {
        step.dependsOn.forEach((dependency) => {
            if (!ids.has(dependency)) {
                context.addIssue({
                    code: zod_1.z.ZodIssueCode.custom,
                    path: ['steps', index, 'dependsOn'],
                    message: `Unknown rollout step dependency ${dependency}`,
                });
            }
            else if (plan.steps.findIndex((candidate) => candidate.stepId === dependency) >= index) {
                context.addIssue({
                    code: zod_1.z.ZodIssueCode.custom,
                    path: ['steps', index, 'dependsOn'],
                    message: `Rollout step dependency ${dependency} must precede its consumer`,
                });
            }
        });
    });
    if (new Set(plan.componentVersionIds).size !== plan.componentVersionIds.length) {
        context.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['componentVersionIds'],
            message: 'Requested component versions must be unique',
        });
    }
    if (new Set(plan.dependencyOrder).size !== plan.dependencyOrder.length) {
        context.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['dependencyOrder'],
            message: 'Dependency order must contain unique versions',
        });
    }
    plan.componentVersionIds.forEach((componentVersionId, index) => {
        if (!dependencyOrder.has(componentVersionId)) {
            context.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                path: ['componentVersionIds', index],
                message: `Requested component version ${componentVersionId} is absent from dependency order`,
            });
        }
    });
});
exports.ProvisionComponentRolloutWork = zod_1.z
    .object({
    rolloutTargetId: zod_1.z.string().min(1),
    rolloutId: zod_1.z.string().min(1),
    applicationInstanceId: zod_1.z.string().min(1),
    attempt: zod_1.z.number().int().positive(),
    leaseExpiresAt: zod_1.z.string().datetime(),
    state: provision_js_1.ProvisionUpdateState,
    plan: exports.ProvisionComponentRolloutPlan,
    completedStepIds: zod_1.z.array(zod_1.z.string().min(1).max(255)).max(4096),
})
    .strict();
exports.ProvisionComponentRolloutStepResult = zod_1.z
    .object({
    stepId: zod_1.z.string().min(1).max(255),
    state: zod_1.z.enum(['succeeded', 'failed', 'skipped']),
    integrityVerified: zod_1.z.boolean().nullable().optional(),
    health: zod_1.z.enum(['unknown', 'healthy', 'degraded', 'failed']).nullable().optional(),
    errorCode: zod_1.z
        .string()
        .regex(/^[A-Z0-9_.-]+$/)
        .max(120)
        .nullable()
        .optional(),
})
    .strict();
exports.ProvisionComponentRolloutStateReport = zod_1.z
    .object({
    clientEventId: zod_1.z.string().uuid(),
    attempt: zod_1.z.number().int().positive(),
    planChecksum: zod_1.z.string().regex(/^[a-f0-9]{64}$/),
    state: provision_js_1.ProvisionUpdateState,
    stepResults: zod_1.z.array(exports.ProvisionComponentRolloutStepResult).max(4096),
    errorCode: zod_1.z
        .string()
        .regex(/^[A-Z0-9_.-]+$/)
        .max(120)
        .nullable()
        .optional(),
    reportedAt: zod_1.z.string().datetime(),
})
    .strict();
exports.ProvisionComponentRolloutStateAck = zod_1.z
    .object({
    reportId: zod_1.z.string().min(1),
    rolloutTargetId: zod_1.z.string().min(1),
    state: provision_js_1.ProvisionUpdateState,
    duplicate: zod_1.z.boolean(),
    completedStepIds: zod_1.z.array(zod_1.z.string().min(1).max(255)).max(4096),
    receivedAt: zod_1.z.string().datetime(),
})
    .strict();
//# sourceMappingURL=provision-component-rollout.js.map